import { ChangeEvent, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { TextField } from '@material-ui/core';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import * as Yup from 'yup';
import { useSnackbar } from 'notistack';
import { notificationBell } from 'assets/images';
import CaptchaV2 from 'components/Captchas/CaptchaV2';
import CaptchaV3 from 'components/Captchas/CaptchaV3';
import useCaptchaSuscripcionTer from 'components/Captchas/useCaptchaSuscripcionTer';
import { QueryParams } from 'config';
import NotificacionService from 'components/AcordeonNotificacionTer/NotificacionService';
import { datoNoValido } from 'utils/colorBallsUtils';
import {
  CADENA_VACIA,
  ESPERA_FALLBACK_CAPTCHA_V3_MS,
  FORZAR_FALLBACK_CAPTCHA_V2,
  MAXIMO_CARACTERES_NUMERO_DE_CLIENTE,
  MINIMO_CARACTERES_NUMERO_DE_CLIENTE,
  REGEX_MAXIMO_OCHO_NUMEROS,
  tipoDeSuscripcion,
} from 'utils/constants';
import {
  actualizarCorreoSuscripcionTer,
  actualizarEstadoCaptchaV3,
  actualizarEstadoClienteSuscrito,
  RootState,
} from 'store';
import useStyles from './ModalSuscripcionTerStyles';
import {
  TECLA_ESCAPE,
  ID_TITULO_MODAL_SUSCRIPCION,
  TEST_ID_MODAL_SUSCRIPCION,
  TEST_ID_MODAL_SUSCRIPCION_CAPTCHA,
  TEST_ID_MODAL_SUSCRIPCION_CERRAR,
  TEST_ID_MODAL_SUSCRIPCION_CONFIRMAR,
  TEST_ID_MODAL_SUSCRIPCION_OVERLAY,
} from './outageDashboard.constants';

/**
 * Modal para suscribirse a los cambios de la reposición estimada.
 * Intenta captcha v3; si no hay token a tiempo o falla, mantiene el checkbox v2.
 * @param open indica si el modal está visible
 * @param numeroCaso número de orden del corte
 * @param onClose cierra el modal
 */
const ModalSuscripcionTer = ({ open, numeroCaso, onClose }: { open: boolean; numeroCaso: string; onClose: () => void }) => {
  /**
   * Variable que maneja el uso de estilos.
   */
  const styles = useStyles();

  /**
   * Uso del traductor.
   */
  const { t } = useTranslation();

  /**
   * Despacha acciones al store.
   */
  const dispatch = useDispatch();

  /**
   * Muestra avisos de éxito o error de la suscripción.
   */
  const { enqueueSnackbar } = useSnackbar();

  /**
   * Indica si el flujo activo es captcha v3.
   */
  const estadoCaptchaV3 = useSelector((state: RootState) => state.desconexion.captchaV3Estado);

  /**
   * Token generado por el captcha v3.
   */
  const tokenCaptchaV3 = useSelector((state: RootState) => state.desconexion.tokenCaptchaV3);

  /**
   * Site key v3 configurada en el entorno.
   */
  const tieneClaveCaptchaV3 = Boolean(process.env.REACT_APP_CAPTCHA_KEY_V3);

  /**
   * Estado y callbacks compartidos de captcha v3/v2.
   */
  const {
    captchaV3ListoRef,
    tokenCaptchaV2,
    setTokenCaptchaV2,
    intentarCaptchaV3,
    mostrarCaptchaV2,
    reiniciarCaptchaState,
    setReiniciarCaptchaState,
    activarFallbackCaptchaV2,
    onVerifyCaptchaV3,
    reiniciarEstadoCaptchas,
  } = useCaptchaSuscripcionTer(tieneClaveCaptchaV3);

  /**
   * Parámetros de la URL, para prellenar el número de cliente.
   */
  const query = QueryParams();

  /**
   * Número de cliente recibido por query param.
   */
  const queryNroCliente = query.get('cliente') || CADENA_VACIA;

  /**
   * Estado que deshabilita el botón de envío mientras el formulario no es válido.
   */
  const [validarBoton, setValidarBoton] = useState(true);

  /**
   * Formulario de correo y número de cliente para la suscripción TER.
   */
  const formik = useFormik({
    initialValues: {
      email: CADENA_VACIA,
      numeroCliente: queryNroCliente || CADENA_VACIA,
    },
    validationSchema: Yup.object({
      email: Yup.string().required(t('errors:enviarNotificacionTer.emailRequerido')).email(t('errors:enviarNotificacionTer.email')),
      numeroCliente: Yup.string()
        .required(t('errors:enviarNotificacionTer.numeroServicioRequerido'))
        .length(MAXIMO_CARACTERES_NUMERO_DE_CLIENTE, t('errors:enviarNotificacionTer.numeroServicio'))
        .matches(REGEX_MAXIMO_OCHO_NUMEROS, t('errors:enviarNotificacionTer.numeroServicio')),
    }),
    onSubmit: async () => {
      const suscripcionOk = await NotificacionService.solicitarNotificacionTer(
        estadoCaptchaV3,
        estadoCaptchaV3 ? tokenCaptchaV3 : tokenCaptchaV2,
        formik.values.email,
        formik.values.numeroCliente,
        numeroCaso,
        tipoDeSuscripcion,
        t,
        enqueueSnackbar,
        dispatch,
        actualizarEstadoClienteSuscrito,
        actualizarEstadoCaptchaV3,
        setReiniciarCaptchaState,
        actualizarCorreoSuscripcionTer
      );
      if (suscripcionOk) {
        onClose();
        return;
      }
      /**
       * Tras error (p. ej. 403 Captcha invalido), el token v3 ya no sirve:
       * no reenviarlo; forzar checkbox v2 para el reintento.
       */
      activarFallbackCaptchaV2(FORZAR_FALLBACK_CAPTCHA_V2);
    },
  });

  /**
   * Reinicia formulario y captchas al abrir/cerrar; al abrir se intenta v3 de nuevo.
   */
  useEffect(() => {
    if (!open) {
      formik.resetForm({
        values: {
          email: CADENA_VACIA,
          numeroCliente: queryNroCliente ? queryNroCliente.replace(/\D/g, CADENA_VACIA) : CADENA_VACIA,
        },
      });
      reiniciarEstadoCaptchas();
      return undefined;
    }

    reiniciarEstadoCaptchas();

    if (!tieneClaveCaptchaV3) {
      return undefined;
    }

    /**
     * Si v3 no entrega token a tiempo, se muestra el checkbox v2.
     */
    const temporizadorFallback = window.setTimeout(() => {
      if (!captchaV3ListoRef.current) {
        activarFallbackCaptchaV2();
      }
    }, ESPERA_FALLBACK_CAPTCHA_V3_MS);

    return () => window.clearTimeout(temporizadorFallback);
    /**
     * formik no va en las dependencias: el reset solo corre al abrir o cerrar.
     */
  }, [open, queryNroCliente, tieneClaveCaptchaV3, activarFallbackCaptchaV2, reiniciarEstadoCaptchas, captchaV3ListoRef]);

  /**
   * Habilita el botón si el formulario es válido y hay token v3 o v2 según el flujo activo.
   */
  useEffect(() => {
    const tieneCaptcha = estadoCaptchaV3 ? !datoNoValido(tokenCaptchaV3) : mostrarCaptchaV2 && !datoNoValido(tokenCaptchaV2);
    setValidarBoton(!formik.isValid || datoNoValido(formik.values.email) || datoNoValido(formik.values.numeroCliente) || !tieneCaptcha);
  }, [formik.isValid, formik.values.email, formik.values.numeroCliente, tokenCaptchaV2, tokenCaptchaV3, estadoCaptchaV3, mostrarCaptchaV2]);

  /**
   * Cierra el modal al presionar Escape mientras está abierto.
   */
  useEffect(() => {
    if (!open) {
      return undefined;
    }
    /**
     * Cierra el modal si la tecla es Escape.
     */
    const onKey = (event: KeyboardEvent) => {
      if (event.key === TECLA_ESCAPE) {
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) {
    return null;
  }

  /**
   * Deja solo dígitos y limita el largo del número de cliente.
   * @param event cambio del input de número de cliente
   */
  const onHandleChangeNumeroCliente = (event: ChangeEvent<HTMLInputElement>) => {
    const numericValue = event.target.value.replace(/\D/g, CADENA_VACIA);
    const limitedValue = numericValue.substring(MINIMO_CARACTERES_NUMERO_DE_CLIENTE, MAXIMO_CARACTERES_NUMERO_DE_CLIENTE);
    formik.setFieldValue('numeroCliente', limitedValue);
  };

  /**
   * Cierra el modal solo si el clic fue en el overlay, no en la tarjeta.
   * @param event clic del overlay
   */
  const handleOverlayClick = (event: { target: EventTarget; currentTarget: EventTarget }) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  /**
   * El portal evita que el logo del header quede por encima del overlay.
   */
  return createPortal(
    <div className={styles.overlay} onClick={handleOverlayClick} data-testid={TEST_ID_MODAL_SUSCRIPCION_OVERLAY} role="presentation">
      <div
        className={styles.card}
        role="dialog"
        aria-modal
        aria-labelledby={ID_TITULO_MODAL_SUSCRIPCION}
        data-testid={TEST_ID_MODAL_SUSCRIPCION}
      >
        <div className={styles.header}>
          <img src={notificationBell} alt={CADENA_VACIA} className={styles.iconoCampana} aria-hidden />
          <p id={ID_TITULO_MODAL_SUSCRIPCION} className={styles.titulo}>
            {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.titulo')}
          </p>
          <div className={styles.textos}>
            <p className={styles.texto}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.parrafo1')}</p>
            <span className={styles.textoSpacer} aria-hidden />
            <p className={styles.texto}>{t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.parrafo2')}</p>
          </div>
        </div>

        <form
          className={styles.form}
          onSubmit={event => {
            event.preventDefault();
            formik.handleSubmit();
          }}
        >
          <div className={styles.campos}>
            <TextField
              className={styles.inputRoot}
              name="email"
              value={formik.values.email}
              label={t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.email')}
              variant="outlined"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.email && Boolean(formik.errors.email)}
              helperText={formik.touched.email && formik.errors.email}
              inputProps={{ 'data-testid': 'modal-suscripcion-email' }}
            />
            <TextField
              className={styles.inputRoot}
              name="numeroCliente"
              value={formik.values.numeroCliente}
              label={t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.numeroCliente')}
              variant="outlined"
              onChange={onHandleChangeNumeroCliente}
              onBlur={formik.handleBlur}
              error={formik.touched.numeroCliente && Boolean(formik.errors.numeroCliente)}
              helperText={formik.touched.numeroCliente && formik.errors.numeroCliente}
              inputProps={{
                'data-testid': 'modal-suscripcion-nro-cliente',
                maxLength: MAXIMO_CARACTERES_NUMERO_DE_CLIENTE,
                inputMode: 'numeric',
              }}
            />
          </div>

          {intentarCaptchaV3 && (
            <div className={styles.captchaWrap} aria-hidden>
              <CaptchaV3 onVerify={onVerifyCaptchaV3} onError={activarFallbackCaptchaV2} />
            </div>
          )}

          {mostrarCaptchaV2 && (
            <div className={styles.captchaV2Wrap} data-testid={TEST_ID_MODAL_SUSCRIPCION_CAPTCHA}>
              <CaptchaV2
                setTokenCaptchaV2={setTokenCaptchaV2}
                reiniciarCaptchaState={reiniciarCaptchaState}
                setReiniciarCaptchaState={setReiniciarCaptchaState}
              />
            </div>
          )}
          <div className={styles.acciones}>
            <button type="button" className={styles.botonCerrar} onClick={onClose} data-testid={TEST_ID_MODAL_SUSCRIPCION_CERRAR}>
              {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.cerrar')}
            </button>
            <button
              type="submit"
              className={styles.botonConfirmar}
              disabled={validarBoton}
              data-testid={TEST_ID_MODAL_SUSCRIPCION_CONFIRMAR}
            >
              {t('desconexion:paginas.mapaCortes.modalTracking.dashboard.modalSuscripcion.confirmar')}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};

export default ModalSuscripcionTer;
